import { useState, useRef } from "react";
import { Table, Spin, DatePicker } from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import CalendarIcon from "../../assets/img/svg/xCalendaricon.svg?react";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { isTrackingBlocked } from "../../services/auditLogGate";
import { CSVLink } from "react-csv";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";

const UserAuditQueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const { accessToken } = useAuthToken();
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [startfloatLabel, setStartfloatLabel] = useState("");
    const [endfloatLabel, setEndfloatLabel] = useState("");
    const [errors, setErrors] = useState({});
    const [csvData, setCsvData] = useState([]);
    const csvLinkRef = useRef(null);
    const [hasGenerated, setHasGenerated] = useState(false);

    const columns = [
        {
            title: "User Name",
            dataIndex: "userName",
            key: "userName",
            width: 220,
            render: (value) => value || "-",
        },
        {
            title: "Event Type",
            dataIndex: "eventType",
            key: "eventType",
            width: 180,
            render: (value) => value || "-",
        },
        {
            title: "Resource Name",
            dataIndex: "resourceName",
            key: "resourceName",
            width: 260,
            render: (value) => value || "-",
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 220,
            render: (value) => {
                if (!value) return "-";
                const parsed = new Date(value);
                return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
            },
        },
        {
            title: "User Role",
            dataIndex: "userRole",
            key: "userRole",
            width: 220,
            render: (value) => value || "-",
        },
        {
            title: "IP Address",
            dataIndex: "ipAddress",
            key: "ipAddress",
            width: 180,
            render: (value) => value || "-",
        },
        {
            title: "User Agent",
            dataIndex: "userAgent",
            key: "userAgent",
            width: 180,
            render: (value) => value || "-",
        },
    ];

    const validateDateRange = () => {
        const nextErrors = {};

        if (
            selectedStartDate &&
            selectedEndDate &&
            selectedEndDate.isBefore(selectedStartDate, "day")
        ) {
            nextErrors.datevalidate = "End Date cannot be lesser than Start Date";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };

    const toSqlDate = (dayjsDate) => {
        if (!dayjsDate) return "";
        return dayjsDate.format("YYYY-MM-DD");
    };

    const buildQueryParams = ({ pag, sorter, exportAll = false }) => {
        const params = new URLSearchParams();
        params.set("FromDate", toSqlDate(selectedStartDate));
        params.set("ToDate", toSqlDate(selectedEndDate));
        params.set("PageNumber", String(pag?.current || 1));
        params.set("PageSize", String(pag?.pageSize || 10));
        params.set("ExportAll", exportAll ? "true" : "false");

        if (sorter?.field) {
            params.set("OrderByColumn", sorter.field);
            params.set("OrderBy", sorter.order === "ascend" ? "ASC" : "DESC");
        }

        return params;
    };

    const fetchData = async ({ pagination: pag, sorter, exportAll = false }) => {
        setLoading(true);

        try {
            if (await isTrackingBlocked()) {
                if (!exportAll) {
                    setData([]);
                    setPagination((prev) => ({ ...prev, total: 0 }));
                }
                return [];
            }

            const queryParams = buildQueryParams({ pag, sorter, exportAll });
            const response = await fetch(`${CommonObj.Endpoints.GetAuditLog}?${queryParams.toString()}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const result = await response.json();
            const rows = Array.isArray(result) ? result : [];
            const formattedData = rows.map((item, index) => ({
                ...item,
                key: `${item.userName || "user"}-${item.createdAt || "created"}-${index}`,
            }));

            if (exportAll) {
                return formattedData;
            }

            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;

            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (error) {
            console.error(error);
            if (!exportAll) {
                setData([]);
                setPagination((prev) => ({ ...prev, total: 0 }));
            }
            message.error("Failed to load user audit logs.");
            return [];
        } finally {
            setLoading(false);
        }

        return [];
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        setSortedInfo(sorter || {});

        const updatedPagination = {
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        };

        setPagination((prev) => ({
            ...prev,
            current: updatedPagination.current,
            pageSize: updatedPagination.pageSize,
        }));

        if (!hasGenerated) {
            return;
        }

        fetchData({ pagination: updatedPagination, sorter });
    };

    const handleStartDateChange = (date) => {
        setSelectedStartDate(date);
        setStartfloatLabel(date ? "float-label" : "");

        if (errors.datevalidate) {
            setErrors({});
        }
    };

    const handleEndDateChange = (date) => {
        setSelectedEndDate(date);
        setEndfloatLabel(date ? "float-label" : "");

        if (errors.datevalidate) {
            setErrors({});
        }
    };

    const handleCleardate = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartfloatLabel("");
        setEndfloatLabel("");
        setErrors({});
        setHasGenerated(false);
        setData([]);

        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));
    };

    const handleGenerate = (e) => {
        e.preventDefault();

        if (!validateDateRange()) {
            return;
        }

        setHasGenerated(true);
        const resetPagination = { current: 1, pageSize: pagination.pageSize };
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchData({ pagination: resetPagination, sorter: sortedInfo });
    };

    const handleExport = async (e) => {
        e.preventDefault();

        if (!validateDateRange()) {
            return;
        }

        const exportRows = await fetchData({
            pagination: { current: 1, pageSize: pagination.pageSize },
            sorter: sortedInfo,
            exportAll: true,
        });

        if (!exportRows.length) {
            message.info("No records available to export.");
            return;
        }

        setCsvData(exportRows);
        setTimeout(() => {
            csvLinkRef.current?.link?.click();
        }, 0);
    };

    if (loading) {
        return <Spin size="large" style={{ margin: "20px auto" }} />;
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>User Audit Log</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container"></div>
                </div>
            </div>

            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg>
                        </span>
                        Select Date Range
                    </div>
                    <div className="advance-filter-action"></div>
                </div>

                <div className="advance-filter-body-container">
                    <div className="advance-filter-input-container">
                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <DatePicker
                                    variant="underlined"
                                    placeholder=""
                                    className={`${"book-page-form-input-text"} ${selectedStartDate === null ? "" : "has-text"}`}
                                    suffixIcon={<CalendarIcon />}
                                    value={selectedStartDate}
                                    onChange={handleStartDateChange}
                                    disabledDate={disableFutureDates}
                                    allowClear
                                    id="startdate-report-1"
                                />
                                <label className={startfloatLabel} htmlFor="startdate-report-1">
                                    Start Date
                                </label>
                                {errors.datevalidate && (
                                    <span className="error-msg-1">{errors.datevalidate}</span>
                                )}
                            </div>
                        </div>
                        <div className="advance-filter-input-width">
                            <div className="field-group">
                                <DatePicker
                                    variant="underlined"
                                    placeholder=""
                                    className={`${"book-page-form-input-text"} ${selectedEndDate === null ? "" : "has-text"}`}
                                    suffixIcon={<CalendarIcon />}
                                    value={selectedEndDate}
                                    onChange={handleEndDateChange}
                                    disabledDate={disableFutureDates}
                                    allowClear
                                    id="enddate-report-1"
                                />
                                <label htmlFor="enddate-report-1" className={endfloatLabel}>
                                    End Date
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="advance-filter-button-container">
                        <button className="cancel-button-gray-outline" onClick={handleCleardate}>
                            Clear
                        </button>
                        <button className="submit-button-gradiant-outline" onClick={handleGenerate}>
                            Generate
                        </button>
                        <button className="submit-button-gradiant-outline" data-track="queue-user-audit-export-csv" onClick={handleExport}>
                            Export as CSV
                        </button>
                        <CSVLink
                            data={csvData}
                            headers={columns.map((col) => ({
                                label: col.title,
                                key: col.dataIndex,
                            }))}
                            filename="UserAuditQueue.csv"
                            style={{ display: "none" }}
                            ref={csvLinkRef}
                        />
                    </div>
                </div>
            </div>

            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters: {},
                                    sortedInfo,
                                }),
                        }}
                        rowKey={(record) => record.key}
                        onChange={handleTableChange}
                        scroll={{
                            x: "max-content",
                            y: "calc(100vh - 280px)",
                        }}
                        rowClassName={(_, index) => (index % 2 === 0 ? "odd-row" : "even-row")}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserAuditQueue;
